import React, { forwardRef } from 'react'
import { Badge } from '../ui/badge'
import { EarnedPoints, PendingPoints } from './BracketPointsDisplay'

/**
 * The outer match card shell used in bracket visualization.
 * Renders the card wrapper with correct state classes, the header with
 * round badge and points display, and children (pick cells / actual winner cells).
 *
 * Props:
 *  - matchId: unique key / identifier for the match
 *  - roundNum: round number (0 for PQ, 1+)
 *  - matchNumber: match number within the round
 *  - isPickCorrect: user's pick was correct
 *  - isPickIncorrect: user's pick was wrong
 *  - isChampionMatch: match involves the user's champion pick
 *  - selectedTeamEliminated: user's selected team was eliminated (suppresses pending points)
 *  - hasOfficialWinner: match has an official result
 *  - hasPick: user made a pick for this match
 *  - points: point value for this round
 *  - cardMinHeight: minimum card height in px
 *  - topPosition: absolute top position for tree layout (ignored in compact)
 *  - cardWidth: card width in px (ignored in compact)
 *  - isCompact: compact/list layout mode
 *  - isMobile: mobile layout mode
 *  - children: cell components to render inside the card
 */
const BracketMatchCard = forwardRef(function BracketMatchCard({
  matchId,
  roundNum,
  matchNumber,
  isPickCorrect = false,
  isPickIncorrect = false,
  isChampionMatch = false,
  selectedTeamEliminated = false,
  hasOfficialWinner = false,
  hasPick = false,
  points,
  cardMinHeight,
  topPosition,
  cardWidth,
  isCompact = false,
  isMobile = false,
  children,
}, ref) {
  const cardStateClass = isPickCorrect
    ? `bracket-match-card--correct${isChampionMatch ? ' bracket-match-card--champion' : ''}`
    : isPickIncorrect
    ? 'bracket-match-card--wrong'
    : isChampionMatch
    ? 'bracket-match-card--champion'
    : ''

  const isFlat = isCompact || isMobile

  let pointsDisplay = null
  if (isPickCorrect) {
    pointsDisplay = <EarnedPoints value={points} />
  } else if (hasPick && !hasOfficialWinner && !selectedTeamEliminated) {
    pointsDisplay = <PendingPoints value={points} />
  }

  const badgeLabel = roundNum === 0
    ? `PQ-${matchNumber}`
    : `R${roundNum}-${matchNumber}`

  return (
    <div
      ref={ref}
      className={`bracket-match-card bracket-match-card-glass ${!isMobile ? 'bracket-match-card--hoverable' : ''} border border-[var(--color-bracket-card-border)] rounded-lg ${isFlat ? 'relative w-full' : 'absolute left-0 bracket-match-card--positioned'} ${cardStateClass} flex flex-col overflow-hidden`}
      style={{
        '--bracket-card-min-height': `${cardMinHeight}px`,
        ...(!isFlat && {
          '--bracket-card-top': `${topPosition}px`,
          '--bracket-card-width': `${cardWidth}px`,
        }),
      }}
    >
      {/* Match Header — locked to exactly 38px so BracketConnectorLines'
          CARD_HEADER_HEIGHT constant matches the rendered DOM. Without this
          lock the natural header height is ~35px (text-xs Badge + py-1.5 +
          border-b), and the 3px drift propagates into every connector's Y. */}
      <div className="h-[38px] px-3 border-b border-[var(--color-bracket-card-border)] bg-muted/30 flex-shrink-0 flex items-center">
        <div className="flex items-center justify-between w-full">
          <Badge variant="secondary" className="bracket-round-badge text-xs">
            {badgeLabel}
          </Badge>
          {pointsDisplay}
        </div>
      </div>

      {children}
    </div>
  )
})

export default BracketMatchCard
